/**
 * Be careful when import here from index files
 * There are already two situations where such imports messed with legacy ts-checks
 * If you are doing such import then makes sure ts-check is ran
 */
import {useCallback, useEffect} from 'react';
import {createSharedStore} from '../sharedStore';
import {COOKIE_CONSENT_EVENT, SERVICE_CONSENT_EVENT} from './constants';
// eslint-disable-next-line monorepo/no-internal-import
import {useHydrationDone} from '@aboutyou/lazy-imports';
// eslint-disable-next-line monorepo/no-internal-import
import {setCookie} from '@aboutyou/utils/src/Cookies/setCookie';

export const AY_COOKIE_CONSENT_GIVEN = 'ay_cookie_consent_given';

interface ServiceConsent {
  name: string;
  consent: boolean;
}
interface UcData {
  consent: {
    services: Record<string, ServiceConsent>;
  };
}

/**
 * used only for consent feature switching
 * Feature flag is here just to allow for merge to production while we are testing logic
 * It will be removed shortly after release
 */
export const getIsExperimentEnabledByDefault = ({
  featureConfig,
  name,
}: {
  featureConfig?: any;
  name: string;
}) => {
  if (
    featureConfig &&
    featureConfig.mergedConfig[name] &&
    featureConfig.mergedConfig[name].defaultValue
  ) {
    return true;
  }

  // on browser we want to use config off window to get right defautl value
  if (process.browser && window.__STATIC_CONFIG__) {
    if (
      window.__STATIC_CONFIG__.featureFlagConfig.mergedConfig[name]
        ?.defaultValue
    ) {
      return true;
    }
  }

  return false;
};

export interface UcEvent {
  location?: string;
  action?: 'impression' | 'close' | 'cancel' | 'accept';
  feature?: string;
  detail?: {
    // only available on UC_UI_VIEW_CHANGED
    previousView: string;
    view: string;

    // only available on UC_UI_CMP_EVENT
    source: string;
    type: string;
    abTestVariant?: string;

    categories: {
      essential: {
        dps: Record<string, boolean>;
        essential: true;
        state: ConsentStatus;
      };
      functional: {
        dps: Record<string, boolean>;
        essential: true;
        state: ConsentStatus;
      };
      marketing: {
        dps: Record<string, boolean>;
        essential: true;
        state: ConsentStatus;
      };
    };
    consent: {
      status: ConsentStatus;
      type: 'EXPLICIT' | 'IMPLICIT';
    };
    services: Record<
      string,
      {
        category: string;
        consent: {given: boolean; type: '<CONSENT_TYPE>'};
        essential: boolean;
        name: string;
      }
    >;
  };
}

export enum ConsentServices {
  Storage = 'c1T8aadmHST2e4',
  Rokt = 'kPgjv2Rg3Z6Bvp',
  Bambuser = 'A4TbXQodU7UxIt',
  FitAnalytics = 'FC6GWdH7TIEGGz',
  Sovendus = 'ZuwXAWsq651MMJ',
  DatadogInfoMonitoring = 'pOs9na2S9qHDXc',
  DatadogCrashMonitoring = '7TdtjOvX9vOs-9', // this is turned on by default and cannot be turned off
  BasicTracking = 'M_EOROGk2Pg9ZR',
  Firebase = 'Ch6HIiSEZrdU21',
  Personalisation = 'MMbRBi46puDo2F',
  Fitmatch = 'r7LnYskY0CqvHp',
  Seznam = 'kAqRxxiDcnLaSv',
  ABTesting = 'Si_j023udEMO37',
  Tradedoubler = 'TmUaymd3WtRUqj',
  GA4 = 'b3q9Is0dImI96G',
}

interface ConsentBannerState {
  updateService: (serviceConsent: Record<string, boolean>) => void;
  serviceConsent: Record<string, boolean>;
  consentViewClosed: boolean;
  updateConsentViewClosed: (isClosed: boolean) => void;
}

// url to docs: https://docs.usercentrics.com/#/v2-events
enum ConsentStatus {
  ALL_ACCEPTED = 'ALL_ACCEPTED',
  ALL_DENIED = 'ALL_DENIED',
  SOME_ACCEPTED = 'SOME_ACCEPTED', // only triggers when user goes to settings and disable or enable something specific\
  SOME_DENIED = 'SOME_DENIED',
}

const setAyConsentCookie = (value: string, domain: string | undefined) => {
  setCookie({
    name: AY_COOKIE_CONSENT_GIVEN,
    value,
    expirationInDays: 30,
    path: '/',
    domain,
  });
};

const AY_AB_TESTING_CONSENT_GIVEN = 'ay_ab_testing_consent_given';
// Cookie name is read by the Cloudflare worker (shop_frontend), keep it in sync when renaming
const setAyABTestingConsentCookie = (
  value: string,
  domain: string | undefined,
) => {
  setCookie({
    name: AY_AB_TESTING_CONSENT_GIVEN,
    value,
    expirationInDays: 30,
    path: '/',
    domain,
  });
};

const removeConsentCookies = () => {
  setCookie({
    name: AY_AB_TESTING_CONSENT_GIVEN,
    value: '0',
    expirationInDays: -30,
    path: '/',
  });
  setCookie({
    name: AY_COOKIE_CONSENT_GIVEN,
    value: '0',
    expirationInDays: -30,
    path: '/',
  });
};

const useUpdateStoreWithConsent = (defaultCookieDomain: string | undefined) => {
  const updateConsentServices = useConsentBannerStateStore(
    state => state.updateService,
  );
  const updateConsentViewClosed = useConsentBannerStateStore(
    state => state.updateConsentViewClosed,
  );

  return useCallback(
    ({
      serviceConsent,
      type,
      status,
      isStorageConsentGiven,
      isABTestingConsentGiven,
    }: {
      serviceConsent: Record<string, boolean>;
      type: 'IMPLICIT' | 'EXPLICIT';
      status: ConsentStatus;
      isStorageConsentGiven?: boolean;
      isABTestingConsentGiven?: boolean;
    }) => {
      window.dispatchEvent(
        new CustomEvent(SERVICE_CONSENT_EVENT, {
          detail: serviceConsent,
        }),
      );
      switch (type) {
        case 'IMPLICIT':
          // eslint-disable-next-line local-rules/set-state-in-use-effect
          updateConsentViewClosed(false);
          break;
        case 'EXPLICIT':
          updateConsentViewClosed(true);
          break;
      }

      switch (status) {
        case ConsentStatus.ALL_DENIED:
        case ConsentStatus.SOME_DENIED:
        case ConsentStatus.SOME_ACCEPTED: {
          // eslint-disable-next-line local-rules/set-state-in-use-effect
          updateConsentServices(serviceConsent);
          removeConsentCookies();
          if (
            // eventData.detail.services[ConsentServices.Storage]?.consent.given &&
            isStorageConsentGiven &&
            status !== ConsentStatus.ALL_DENIED
          ) {
            setAyConsentCookie('1', defaultCookieDomain);
            if (
              // eventData.detail.services[ConsentServices.ABTesting]?.consent
              //   .given
              isABTestingConsentGiven
            ) {
              setAyABTestingConsentCookie('1', defaultCookieDomain);
            }
          }
          break;
        }
        case ConsentStatus.ALL_ACCEPTED: {
          updateConsentServices(serviceConsent);
          setAyConsentCookie('1', defaultCookieDomain);
          setAyABTestingConsentCookie('1', defaultCookieDomain);
          break;
        }
      }
    },
    [updateConsentServices, updateConsentViewClosed, defaultCookieDomain],
  );
};

export const useUpdateConsentBannerAcceptedState = (
  defaultCookieDomain: string | undefined,
) => {
  const updateStore = useUpdateStoreWithConsent(defaultCookieDomain);
  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    const handler = (event: Event) => {
      const eventData = event as unknown as UcEvent;
      const {status, type} = eventData.detail!.consent;

      const serviceConsent: Record<string, boolean> = Object.entries(
        eventData.detail!.services,
      ).reduce(
        (acc, [key, value]) => ({...acc, [key]: value.consent.given}),
        {},
      );

      // eslint-disable-next-line local-rules/set-state-in-use-effect
      updateStore({
        status,
        type,
        serviceConsent,
        isStorageConsentGiven: serviceConsent[ConsentServices.Storage],
        isABTestingConsentGiven: serviceConsent[ConsentServices.ABTesting],
      });
    };
    const shouldTakeConsentValuesWithoutWaitingForEvent =
      'UC_UI' in window &&
      window.UC_UI!.isInitialized() &&
      '__ucCmp' in window &&
      window.__ucCmp.cmpController.consent.type === 'EXPLICIT';

    if (shouldTakeConsentValuesWithoutWaitingForEvent) {
      const {status, type} = window.__ucCmp.cmpController.consent;
      const serviceConsent = getServiceConsentObject();
      updateStore({
        status,
        type,
        serviceConsent,
        isStorageConsentGiven: serviceConsent[ConsentServices.Storage],
        isABTestingConsentGiven: serviceConsent[ConsentServices.ABTesting],
      });
    }
    window.addEventListener(COOKIE_CONSENT_EVENT, handler);

    return () => {
      window.removeEventListener(COOKIE_CONSENT_EVENT, handler);
    };
  }, [updateStore]);
};

const getUCDataFromStorage = (): UcData | undefined => {
  const ucData = localStorage.getItem('ucData');
  if (!ucData) {
    return undefined;
  }

  return JSON.parse(ucData);
};

const getServiceConsentObject = () => {
  const parsedUcData = getUCDataFromStorage();
  if (!parsedUcData) {
    return {};
  }

  const result: Record<string, boolean> = Object.entries(
    parsedUcData?.consent.services,
  ).reduce((acc, [key, value]) => {
    return {
      ...acc,
      [key]: (value as ServiceConsent).consent,
    };
  }, {});

  return result;
};

export const useConsentBannerStateStore = createSharedStore<ConsentBannerState>(
  set => {
    if (!process.browser) {
      return {
        serviceConsent: {},
        updateService: serviceConsent => {
          set({serviceConsent});
        },
        updateConsentViewClosed: () => {
          set({consentViewClosed: false});
        },
        consentViewClosed: false,
      };
    }

    return {
      serviceConsent: getServiceConsentObject(),
      consentViewClosed: false,
      updateService: serviceConsent => {
        set({serviceConsent});
      },
      updateConsentViewClosed: (isClosed: boolean) => {
        set({consentViewClosed: isClosed});
      },
    };
  },
);

/**
 * Use this only if you need to know if the user has accepted the cookie banner during the render time.
 * Using this hook will cause a rerender of the component when the user accepts/rejects the cookie banner.
 * If you only need to check if the user has accepted the cookies during a callback event (eg: button click) use `getIsConsentBannerAccepted` instead since you dont need a rerender of the component in that case.
 */
const useIsServiceConsentAccepted = (name: string) => {
  const isHydrationDone = useHydrationDone();
  const serviceConsent = useConsentBannerStateStore(
    state => state.serviceConsent,
  );

  if (!isHydrationDone) {
    return false;
  }

  return Boolean(serviceConsent[name]);
};

const getServiceConsent = (name: string) => {
  const valueFromStore =
    useConsentBannerStateStore.getState().serviceConsent[name];

  return valueFromStore;
};

export const getIsStorageConsentGiven = () => {
  return getServiceConsent(ConsentServices.Storage);
};

export const getIsDatadogInfoLogsConsentGiven = () => {
  return getServiceConsent(ConsentServices.DatadogInfoMonitoring);
};

export const getIsDatadogPersonalisationConsentGiven = () => {
  return getServiceConsent(ConsentServices.Personalisation);
};

export const getIsDatadogCrashLogsConsentGiven = () => {
  return getServiceConsent(ConsentServices.DatadogCrashMonitoring);
};

export const getIsBasicTrackingConsentGiven = () => {
  return getServiceConsent(ConsentServices.BasicTracking);
};

export const getIsSeznamConsentGiven = () => {
  return getServiceConsent(ConsentServices.Seznam);
};

export const getIsFirebaseConsentGiven = () => {
  return (
    getServiceConsent(ConsentServices.Firebase) && getIsStorageConsentGiven()
  );
};

export const useIsStorageConsentGiven = () => {
  const consent = useIsServiceConsentAccepted(ConsentServices.Storage);
  return consent;
};

export const useIsDataInfoMonitoringConsentGiven = () => {
  const consent = useIsServiceConsentAccepted(
    ConsentServices.DatadogInfoMonitoring,
  );
  return consent;
};

export const useIsABTestingConsentGiven = () => {
  const storageConsent = useIsStorageConsentGiven();
  const consent = useIsServiceConsentAccepted(ConsentServices.ABTesting);
  return consent && storageConsent;
};

export const useIsRoktConsetGiven = () => {
  const consent = useIsServiceConsentAccepted(ConsentServices.Rokt);
  return consent;
};

export const useIsBambuserConsetGiven = () => {
  const consent = useIsServiceConsentAccepted(ConsentServices.Bambuser);
  return consent;
};

export const useIsFitAnalyticsConsetGiven = () => {
  const consent = useIsServiceConsentAccepted(ConsentServices.FitAnalytics);
  return consent;
};

export const useIsSovendusConsentGiven = () => {
  const consent = useIsServiceConsentAccepted(ConsentServices.Sovendus);
  return consent;
};
export const useIsTradedoublerConsentGiven = () => {
  const consent = useIsServiceConsentAccepted(ConsentServices.Tradedoubler);
  return consent;
};

export const useIsGA4ConsentGiven = () => {
  const consent = useIsServiceConsentAccepted(ConsentServices.GA4);
  return consent;
};

export const useIsFirebaseConsentGiven = () => {
  // firebase functionality is saving to storage
  const consent = useIsServiceConsentAccepted(ConsentServices.Firebase);
  const storageConsent = useIsStorageConsentGiven();

  return consent && storageConsent;
};

export const useIsPersonalisationConsentGiven = () => {
  const consent = useIsServiceConsentAccepted(ConsentServices.Personalisation);

  return consent;
};

export const useIsConsentBannerClosed = () => {
  return useConsentBannerStateStore().consentViewClosed;
};

export const useGetAcceptedState = () => {
  const consents = useConsentBannerStateStore().serviceConsent;

  return useCallback((): boolean | string => {
    const entries = Object.entries(consents);

    if (entries.length === 0) {
      return false;
    }

    let isAllTrue = true;
    const result: string[] = [];

    entries.forEach(([key, value]) => {
      if (!value) {
        isAllTrue = false;
        return;
      }

      result.push(key);
    });

    return isAllTrue || result.join(',');
  }, [consents]);
};
